import React from 'react';
import { Spin, Select } from 'antd';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ApplicationState } from '../../../../reducers';
import { AsyncDispatch } from '../../../../../types/global';
import { fetchReplitAccessToken } from '../../../../actions/replit';
import { ReplitIntegration } from '../../../../reducers/tools/ReplitReducer';
import { hasPermission } from '../../../../utils/Permissions';
import { useAppDispatch } from '../../../../store/ReduxStore';
import { SPECIAL_RECEIVER_APP_NAMES } from '../../../../constants/ReplitConstants';

interface CustomAppsEmbeddedProps {
  brandId?: number;
  receiverId?: number;
  productId?: number;
  brandCode?: string;
  partNumber?: string;
  level?: string;
  appId?: number;
  hideAppSelector?: boolean;
}

const CustomAppsEmbedded: React.FC<CustomAppsEmbeddedProps> = ({
  brandId,
  receiverId,
  productId,
  brandCode,
  partNumber,
  level: levelOverride,
  appId: appIdFilter,
  hideAppSelector = false,
}) => {
  const dispatch: AsyncDispatch = useAppDispatch();
  const { t } = useTranslation();

  const integrations = useSelector((state: ApplicationState) => state.tools.replit.integrations);
  const fetchingIntegrations = useSelector(
    (state: ApplicationState) => state.tools.replit.fetchingIntegrations
  );
  const accessTokens = useSelector((state: ApplicationState) => state.tools.replit.accessTokens);
  const fetchingAccessToken = useSelector(
    (state: ApplicationState) => state.tools.replit.fetchingAccessToken
  );
  const user = useSelector((state: ApplicationState) => state.user.user);
  const configs = useSelector((state: ApplicationState) => state.customAppsSettings.configs);
  const fetchingConfigs = useSelector(
    (state: ApplicationState) => state.customAppsSettings.fetchingConfigs
  );

  const [selectedAppId, setSelectedAppId] = React.useState<number | undefined>();
  const [tokenKey, setTokenKey] = React.useState<string | undefined>();

  const canViewCustomApps = React.useMemo(() => hasPermission(user, 'can_access_replit'), [user]);

  const { level, iframeHeight } = React.useMemo(() => {
    if (levelOverride) {
      return { level: levelOverride, iframeHeight: 'calc(100vh - 86px)' };
    }
    if (receiverId) return { level: 'RECEIVER', iframeHeight: 'calc(100vh - 220px)' };
    if (productId) return { level: 'PRODUCT', iframeHeight: 'calc(100vh - 300px)' };
    if (brandId) return { level: 'BRAND', iframeHeight: 'calc(100vh - 86px)' };
    return { level: 'ACCOUNT', iframeHeight: 'calc(100vh - 86px)' };
  }, [receiverId, productId, brandId, levelOverride]);

  const apps = React.useMemo(() => {
    if (!integrations) return [];

    if (appIdFilter) {
      return integrations.filter(app => app.id === appIdFilter);
    }

    if (!configs) return [];

    const enabledConfigs = configs.filter(config => config.level === level && config.is_enabled);

    if (enabledConfigs.length === 0) return [];

    const enabledAppIds = enabledConfigs.map(config => config.app_id);

    const filteredApps = integrations.filter(integration => enabledAppIds.includes(integration.id));

    return filteredApps.sort((a, b) => {
      const configA = enabledConfigs.find(c => c.app_id === a.id);
      const configB = enabledConfigs.find(c => c.app_id === b.id);
      return (configA?.display_order || 999) - (configB?.display_order || 999);
    });
  }, [integrations, configs, level, appIdFilter]);

  React.useEffect(() => {
    if (apps.length > 0 && !selectedAppId) {
      setSelectedAppId(apps[0].id);
    }
  }, [apps, selectedAppId]);

  React.useEffect(() => {
    setTokenKey(undefined);
  }, [level]);

  React.useEffect(() => {
    if (selectedAppId && canViewCustomApps && !fetchingAccessToken && user) {
      // For receiver level, check if app_id requires special level handling
      const selectedApp = integrations.find(app => app.id === selectedAppId);
      const isSpecialReceiverApp =
        level === 'RECEIVER' &&
        selectedApp &&
        SPECIAL_RECEIVER_APP_NAMES.includes(selectedApp.app_id);

      // Use app_id as level for special receiver apps (backward compatibility)
      const effectiveLevel = isSpecialReceiverApp ? selectedApp.app_id : level;

      const currentTokenKey = `${selectedAppId}-${effectiveLevel}`;

      if (currentTokenKey !== tokenKey) {
        setTokenKey(currentTokenKey);

        const context = {
          level: effectiveLevel,
          account_id: user.account_id,
          user_id: user.id,
          ...(brandId && { brand_id: brandId }),
          ...(brandCode && { brand_code: brandCode }),
          ...(receiverId && { receiver_id: receiverId }),
          ...(productId && { product_id: productId }),
          ...(partNumber && { part_number: partNumber }),
        };

        dispatch(fetchReplitAccessToken(selectedAppId, context));
      }
    }
  }, [
    dispatch,
    selectedAppId,
    canViewCustomApps,
    tokenKey,
    fetchingAccessToken,
    user,
    level,
    brandId,
    brandCode,
    receiverId,
    productId,
    partNumber,
    integrations,
  ]);

  const createAppUrl = React.useCallback(
    (app: ReplitIntegration): string => {
      const url = new URL(app.app_url);

      // For receiver level, check if app_id requires special level handling
      const isSpecialReceiverApp =
        level === 'RECEIVER' && SPECIAL_RECEIVER_APP_NAMES.includes(app.app_id);

      // Use app_id as level for special receiver apps (backward compatibility)
      const effectiveLevel = isSpecialReceiverApp ? app.app_id : level;

      const currentTokenKey = `${app.id}-${effectiveLevel}`;
      const token = accessTokens[currentTokenKey];

      if (token) {
        url.searchParams.set('token', token);
      }

      if (user?.account_id) {
        url.searchParams.set('account_id', user.account_id.toString());
        url.searchParams.set('user_id', user.id.toString());
      }

      if (effectiveLevel) {
        url.searchParams.set('level', effectiveLevel);
      }

      if (brandId) url.searchParams.set('brand_id', brandId.toString());
      if (brandCode) url.searchParams.set('brand_code', brandCode);
      if (receiverId) url.searchParams.set('receiver_id', receiverId.toString());
      if (productId) url.searchParams.set('product_id', productId.toString());
      if (partNumber) url.searchParams.set('part_number', partNumber);

      return url.toString();
    },
    [accessTokens, user, level, brandId, brandCode, receiverId, productId, partNumber]
  );

  const renderCustomApp = React.useCallback(
    (app: ReplitIntegration) => {
      if (fetchingAccessToken && selectedAppId === app.id) {
        return <Spin className="spinner-center" style={{ marginTop: '20px' }} />;
      }

      const iframeUrl = createAppUrl(app);
      const sandboxPermissions = ['allow-scripts', 'allow-same-origin', 'allow-forms'];

      return (
        <iframe
          src={iframeUrl}
          title={app.app_id}
          width="100%"
          style={{
            border: 'none',
            borderRadius: '6px',
            height: iframeHeight,
            minHeight: '400px',
          }}
          sandbox={sandboxPermissions.join(' ')}
          loading="lazy"
          allow="clipboard-read; clipboard-write"
        />
      );
    },
    [fetchingAccessToken, selectedAppId, createAppUrl, iframeHeight]
  );

  if (!canViewCustomApps) {
    return null;
  }

  if (fetchingIntegrations || fetchingConfigs) {
    return <Spin className="spinner-center" style={{ marginTop: '20px' }} />;
  }

  const selectedApp = apps.find(app => app.id === selectedAppId);

  return (
    <div>
      {!hideAppSelector && (
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ minWidth: 'max-content' }}>{t('retool:appName')}:</span>
          <Select
            value={selectedAppId}
            onChange={setSelectedAppId}
            style={{ minWidth: '200px' }}
            size="small"
            placeholder={t('common:select')}
          >
            {apps.map(app => (
              <Select.Option key={app.id} value={app.id}>
                {app.app_id}
              </Select.Option>
            ))}
          </Select>
        </div>
      )}

      {selectedApp && <div>{renderCustomApp(selectedApp)}</div>}
    </div>
  );
};

export default CustomAppsEmbedded;
